/**
 * @fileoverview ActionRow: the foot every Cancel sits in (CLAUDE.md rule 7; B111, owner decisions Q2, Q6, Q9). AddCancelRow made
 * general: Cancel on the left as the outline button, the action (the children) on the right, filled and wider, both 44px. An
 * optional quiet third button (Remove) sits first, far left, and the Cancel and action pair sits right on the same row when they fit. The row
 * wraps (flex-wrap) and the pair has a minimum width (16rem), so when they do not fit the pair drops under the quiet button, which then sits alone
 * on the left of its own line (owner, 1 Nov 2026). The row's own width decides, not the window's, so a narrow dialog on a wide screen wraps too.
 * The action is always at least as wide as Cancel (it grows 1 to Cancel's 0.7). Every button is drawn by the one Button, so a style changes in
 * one place. AddCancelRow (features/photo-tray) is left as it is; B137 is changing it.
 *
 * Where: every dialog and sheet foot (B111). Layout is decided here only (rule 8): no screen adds classes to it.
 * Words (en/es): sitewide.cancel (and the waiting word through Button loading, when removeLoading: saving, or removeBusy).
 *
 * @module ActionRow
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared/components/ui/button';
import type { ButtonBusy } from '@/shared/components/ui/button';
import { useTranslation } from '@/shared/hooks/useTranslation';

/** ActionRow's props. */
export interface ActionRowProps {
  /** The action button itself (it keeps its own label, loading and disabled state); the row makes it 44px and wider. */
  children: React.ReactNode;
  /** Cancel's handler. When absent, only the action is drawn. */
  onCancel?: () => void;
  /** Cancel's word; defaults to sitewide.cancel. */
  cancelLabel?: string;
  /** True while the card is busy: Cancel waits. */
  disabled?: boolean;
  /** The quiet third button's handler (Remove). Drawn only with removeLabel. */
  onRemove?: () => void;
  /** The quiet third button's word. */
  removeLabel?: string;
  /** True while the quiet third button's errand waits on the server: it shows Button loading (spinner and the saving word). */
  removeLoading?: boolean;
  /** The waiting word on the quiet third button while removeLoading (Button's busy map); saving when absent. */
  removeBusy?: ButtonBusy;
}

/**
 * Cancel and the action, with an optional Remove far left.
 *
 * @param props - ActionRowProps.
 */
export const ActionRow: React.FC<ActionRowProps> = ({ children, onCancel, cancelLabel, disabled = false, onRemove, removeLabel, removeLoading = false, removeBusy }) => {
  const { reactTranslate: t } = useTranslation();
  if (!onCancel) return <>{children}</>;
  const showRemove = Boolean(onRemove && removeLabel);
  return (
    <div className={cn('flex w-full gap-2', showRemove ? 'flex-wrap items-center' : 'items-center')}>
      {showRemove && (
        <Button type="button" variant="ghost" size="lg" loading={removeLoading} busy={removeBusy} disabled={disabled} onClick={onRemove}>{removeLabel}</Button>
      )}
      <div className={cn('flex min-w-0 gap-2 [&>*:first-child]:flex-1 [&>*:first-child]:px-4 [&>*:last-child]:h-11 [&>*:last-child]:flex-1 [&>*:last-child]:grow-[1.4] [&>*:last-child]:px-3 [&>*:last-child]:min-w-0 [&>*:last-child]:whitespace-normal [&>*:last-child]:py-0 [&>*:last-child]:leading-tight', showRemove ? 'ml-auto min-w-[16rem] flex-1' : 'flex-1')}>
        <Button type="button" variant="outline" size="lg" disabled={disabled} onClick={onCancel}>{cancelLabel ?? t('sitewide.cancel')}</Button>
        {children}
      </div>
    </div>
  );
};
